import React, { useState, useEffect } from "react"
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  FlatList,
  Image,
  ScrollView,
  ActivityIndicator
} from "react-native"
import Modal from "modal-enhanced-react-native-web"
import { Feather } from "@expo/vector-icons"

interface Props {
  visible: boolean
  loader: boolean
  issues: any[]
  onClose: () => void
  onConfirm: (payload: { title: string; issue_ids: number[] }) => void
  onRemove: (id: number) => void
}

export default function SelfManageModal({
  visible,
  issues = [],
  onClose,
  onConfirm,
  onRemove,
  loader,
}: Props) {

  const [projectTitle, setProjectTitle] = useState("")
  const [selectedIssues, setSelectedIssues] = useState<any[]>([])

  useEffect(() => {
    setSelectedIssues(issues)
  }, [issues])

  const removeIssue = (id: number) => {
    setSelectedIssues(prev =>
      prev.filter(item =>
        item.open_flow_maintenance_issue_id !== id
      )
    )

    onRemove(id) // 🔥 inform parent
  }
  const handleSubmit = () => {
    if (!projectTitle.trim() || selectedIssues.length === 0) return

    const payload = {
      title: projectTitle,
      issue_ids: selectedIssues.map(
        item => item.open_flow_maintenance_issue_id
      )
    }

    onConfirm(payload)
  }

  return (
    <Modal
      isVisible={visible}
      backdropTransitionInTiming={200}
      backdropTransitionOutTiming={200}
      useNativeDriver={false}
      style={{ margin: 0 }}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>

          {/* HEADER */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Self Manage</Text>
          </View>

          {/* PROJECT TITLE */}
          <View style={styles.inputWrapper}>
            <Text style={styles.label}>Project Title</Text>
            <TextInput
              style={styles.input}
              placeholder="Enter project title"
              value={projectTitle}
              onChangeText={setProjectTitle}
              maxLength={60}
            />
            <Text style={styles.wordCount}>
              {projectTitle.length}/60 words left
            </Text>
          </View>

          {/* ISSUE CARDS */}
          <ScrollView style={{ flex: 1 }}>
            <View style={styles.cardGrid}>
              {selectedIssues.map(issue => (
                <View key={issue.open_flow_maintenance_issue_id} style={styles.card}>

                  {/* TOP ROW */}
                  <View style={styles.cardHeader}>
                    <Text style={styles.issueId}>
                      #{issue.open_flow_maintenance_issue_id}
                    </Text>

                    <TouchableOpacity
                      onPress={() =>
                        removeIssue(issue.open_flow_maintenance_issue_id)
                      }
                    >
                      <Feather name="x-circle" size={20} color="#E53935" />
                    </TouchableOpacity>
                  </View>

                  <Text style={styles.cardLabel}>Report Title</Text>
                  <Text style={styles.cardValue}>
                    {issue.title || "Untitled"}
                  </Text>

                  <Text style={styles.cardLabel}>Date Submitted</Text>
                  <Text style={styles.cardValue}>
                    {new Date(issue.date_opened).toDateString()}
                  </Text>

                  <Text style={styles.cardLabel}>Message</Text>
                  <Text style={styles.cardValue}>
                    {issue.description || "—"}
                  </Text>

                  <Text style={styles.cardLabel}>Annexes</Text>

                  {issue.images?.length > 0 ? (
                    <View style={styles.imageRow}>
                      {issue.images.map((img: string, index: number) => (
                        <Image
                          key={index}
                          source={{ uri: img }}
                          style={styles.image}
                        />
                      ))}
                    </View>
                  ) : (
                    <Text style={styles.noAttachment}>
                      No attachments
                    </Text>
                  )}
                </View>
              ))}
            </View>
          </ScrollView>

          {/* FOOTER */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.cancelBtn}
              onPress={onClose}
            >
              <Text style={styles.cancelText}>CANCEL</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.confirmBtn,
                (!projectTitle || selectedIssues.length === 0) &&
                styles.disabledBtn
              ]}
              onPress={handleSubmit}
              disabled={!projectTitle || selectedIssues.length === 0}
            >
              {loader ? <ActivityIndicator color={"white"} /> :
                <Text style={styles.confirmText}>
                  CONFIRM
                </Text>}
            </TouchableOpacity>
          </View>

        </View>
      </View>
    </Modal>
  )
}
const styles = StyleSheet.create({

  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center"
  },

  container: {
    width: "90%",
    maxHeight: "90%",
    backgroundColor: "#fff",
    borderRadius: 20,
    overflow: "hidden",
    elevation: 10
  },

  header: {
    padding: 25,
    borderBottomWidth: 1,
    borderColor: "#eee"
  },

  headerTitle: {
    fontSize: 22,
    fontWeight: "700"
  },

  inputWrapper: {
    padding: 25
  },

  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8
  },

  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    padding: 12
  },

  wordCount: {
    fontSize: 12,
    color: "#888",
    marginTop: 5
  },

  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
    padding: 20
  },

  card: {
    width: 300,
    backgroundColor: "#F9FAFB",
    borderRadius: 14,
    padding: 18,
    borderWidth: 1,
    borderColor: "#E5E7EB"
  },

  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12
  },

  issueId: {
    fontSize: 18,
    fontWeight: "700"
  },

  cardLabel: {
    fontSize: 13,
    color: "#777",
    marginTop: 10
  },

  cardValue: {
    fontSize: 14,
    fontWeight: "500"
  },

  imageRow: {
    flexDirection: "row",
    marginTop: 8,
    gap: 10
  },

  image: {
    width: 70,
    height: 70,
    borderRadius: 10
  },

  noAttachment: {
    fontSize: 13,
    color: "#999",
    marginTop: 6
  },

  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    padding: 20,
    borderTopWidth: 1,
    borderColor: "#eee",
    gap: 15
  },

  cancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 25,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#8B5CF6"
  },

  cancelText: {
    color: "#8B5CF6",
    fontWeight: "600"
  },

  confirmBtn: {
    backgroundColor: "#8B5CF6",
    paddingVertical: 12,
    paddingHorizontal: 25,
    borderRadius: 10
  },

  confirmText: {
    color: "#fff",
    fontWeight: "600"
  },

  disabledBtn: {
    backgroundColor: "#C4B5FD"
  }
})
